import { useEffect, useState } from "react";
import { ShieldCheck, ChevronDown, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

export default function RecaptchaConfig() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [siteKey, setSiteKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/content/settings").then((r) => {
      const doc = r.data.find((s) => s.key === "recaptcha");
      if (doc) { setEnabled(!!doc.enabled); setSiteKey(doc.site_key || ""); setSecretKey(doc.secret_key || ""); }
    }).catch(() => {});
  }, []);

  const save = async (nextEnabled) => {
    setSaving(true);
    try {
      await api.put("/admin/recaptcha", { enabled: nextEnabled, site_key: siteKey, secret_key: secretKey });
      setEnabled(nextEnabled);
      toast.success(nextEnabled ? "reCAPTCHA activado: claves validadas por Google" : "Configuración guardada (reCAPTCHA desactivado)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-8" data-testid="recaptcha-config-section">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between hud-border bg-white px-5 py-4 hover:bg-slate-50 transition-colors duration-200" data-testid="recaptcha-config-toggle-section">
        <div className="flex items-center gap-3">
          <ShieldCheck size={16} className="text-[#5698C7]" strokeWidth={1.5} />
          <span className="font-mono-tech text-xs tracking-[0.2em] font-semibold text-[#0B1220]">SEGURIDAD · RECAPTCHA V2</span>
          <span className={`font-mono-tech text-[9px] tracking-widest px-2 py-1 rounded ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="recaptcha-status-badge">
            {enabled ? "ACTIVO" : "INACTIVO"}
          </span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="hud-border bg-white border-t-0 px-5 py-5 space-y-4" data-testid="recaptcha-config-panel">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs font-semibold text-[#0B1220]">Protección reCAPTCHA en Login, Contacto y PIN</div>
              <p className="text-[11px] text-slate-400 mt-0.5">Solo se puede activar con claves guardadas y de formato válido (40 caracteres, empiezan por 6L). Con el conmutador apagado, los formularios funcionan sin captcha.</p>
            </div>
            <button type="button" disabled={saving} onClick={() => save(!enabled)} data-testid="recaptcha-enable-toggle" aria-pressed={enabled}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0 ${enabled ? "bg-[#5698C7]" : "bg-slate-300"} ${saving ? "opacity-60" : ""}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="font-mono-tech text-[10px] tracking-widest text-slate-400">SITE KEY (CLAVE DE SITIO WEB)</label>
              <input value={siteKey} onChange={(e) => setSiteKey(e.target.value)} placeholder="6L..." data-testid="recaptcha-site-key-input"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-xs font-mono-tech focus:outline-none focus:border-[#5698C7]" />
            </div>
            <div>
              <label className="font-mono-tech text-[10px] tracking-widest text-slate-400">SECRET KEY (CLAVE SECRETA)</label>
              <input value={secretKey} onChange={(e) => setSecretKey(e.target.value)} placeholder="6L..." type="password" autoComplete="off" data-testid="recaptcha-secret-key-input"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-xs font-mono-tech focus:outline-none focus:border-[#5698C7]" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <p className="text-[11px] text-slate-400">Obtén las claves (v2 "No soy un robot") en google.com/recaptcha/admin — registra el dominio de producción y el de preview.</p>
            <button onClick={() => save(enabled)} disabled={saving} data-testid="recaptcha-save-btn"
              className="inline-flex items-center gap-2 bg-[#5698C7] text-white text-xs font-mono-tech tracking-widest px-4 py-2 hover:bg-[#0B1220] transition-colors duration-200 disabled:opacity-50">
              <Save size={12} /> {saving ? "GUARDANDO..." : "GUARDAR CLAVES"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
